import type { LookupAnswer, LookupHit } from "odyssey-shared";
import { timeoutSignal } from "../../timeout-signal.ts";

/**
 * How a GM's page looks families and characters up (ADR-056's note of 2026-10-08 on the lookup): a GET of
 * `/moderation/lookup` with the page's current session token and no cookie, the answer read field by field, and
 * every answer the page cannot read a failed read.
 */

/**
 * What came of one lookup: the hits and the count found; the session ended; the roles no longer grant it; over a
 * limit; a refusal the gateway explained; or a failure the page could not read.
 */
export type LookupReply = { readonly kind: "ok"; readonly answer: LookupAnswer } | { readonly kind: "session" | "forbidden" | "rate" | "failed" | "unreadable" };

/**
 * One lookup, by what the GM typed, held to the shared rule first.
 */
export type LookupFetch = (pattern: string) => Promise<LookupReply>;

const REQUEST_TIMEOUT_MS = 10_000;

/**
 * Whether a value is a plain object.
 *
 * @param value The value.
 * @returns True for an object that is not an array or null.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Reads one hit against its shape.
 *
 * @param value The hit as sent.
 * @returns The hit, or null when it is out of shape.
 */
function hitOf(value: unknown): LookupHit | null {
  if (!isRecord(value)) {
    return null;
  }
  const { kind, name, family } = value;
  return (kind === "family" || kind === "character") && typeof name === "string" && typeof family === "string" ? { kind, name, family } : null;
}

/**
 * Builds the client.
 *
 * @param options The gateway's `/moderation/lookup` URL, the page's current session token (null when it holds none),
 *   and the fetch, the page's own unless a test gives another.
 * @returns The lookup; it never rejects.
 */
export function lookupClient(options: { readonly endpoint: string; readonly token: () => string | null; readonly fetch?: (input: string, init?: RequestInit) => Promise<Response> }): LookupFetch {
  const send = options.fetch ?? ((input: string, init?: RequestInit) => fetch(input, init));
  return async (pattern) => {
    const token = options.token();
    if (token === null || token === "") {
      return { kind: "session" };
    }
    const limit = timeoutSignal(REQUEST_TIMEOUT_MS);
    try {
      const response = await send(`${options.endpoint}?q=${encodeURIComponent(pattern)}`, { method: "GET", headers: { Authorization: `Bearer ${token}` }, credentials: "omit", signal: limit.signal });
      let body: unknown;
      try {
        body = await response.json();
      } catch {
        return { kind: "unreadable" };
      }
      if (response.status === 200) {
        const hits = isRecord(body) && Array.isArray(body["hits"]) ? body["hits"] : null;
        const total = isRecord(body) ? body["total"] : null;
        return hits === null || typeof total !== "number" ? { kind: "unreadable" } : { kind: "ok", answer: { hits: hits.map(hitOf).filter((hit): hit is LookupHit => hit !== null), total } };
      }
      if (!isRecord(body) || typeof body["error"] !== "string") {
        return { kind: "unreadable" };
      }
      const kinds: Readonly<Record<number, LookupReply["kind"]>> = { 401: "session", 403: "forbidden", 429: "rate" };
      return { kind: (kinds[response.status] ?? "failed") as Exclude<LookupReply["kind"], "ok"> };
    } catch {
      return { kind: "unreadable" };
    } finally {
      limit.settle();
    }
  };
}
